import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fonts, radius } from '@/shared/theme';
import { Icon } from './icon';
import { TileIconButton } from './tile-icon-button';

interface ChartCardProps {
  title: ReactNode;
  /** Line under the title, e.g. the total. */
  subtitle?: ReactNode;
  /** Renders the chart; `expanded` is true inside the full-screen dialog. */
  children: (expanded: boolean) => ReactNode;
}

function CardHead({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action: ReactNode;
}) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: '14px 16px 12px',
        borderBottom: `1px solid ${theme.vars.palette.md.border}`,
      })}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontFamily: fonts.head, fontWeight: 800, fontSize: 15 }}>{title}</Box>
        {subtitle && (
          <Box sx={{ mt: '3px', fontSize: 12.5, color: 'text.secondary', fontWeight: 600 }}>
            {subtitle}
          </Box>
        )}
      </Box>
      {action}
    </Box>
  );
}

interface ChartExpandButtonProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Renders the chart shown full screen. */
  children: () => ReactNode;
}

/** Icon button that opens a chart in a near full-screen dialog. */
export function ChartExpandButton({ title, subtitle, children }: ChartExpandButtonProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TileIconButton label={t('actions.expandChart')} size={32} onClick={() => setExpanded(true)}>
        <Icon name="expand" size={16} />
      </TileIconButton>
      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        fullWidth
        maxWidth={false}
        slotProps={{ paper: { sx: { width: '94vw', maxHeight: '94vh', m: 1 } } }}
      >
        <CardHead
          title={title}
          subtitle={subtitle}
          action={
            <TileIconButton label={t('actions.close')} size={32} onClick={() => setExpanded(false)}>
              <Icon name="x" size={16} />
            </TileIconButton>
          }
        />
        <Box sx={{ p: '16px 20px 12px', overflow: 'auto' }}>{expanded && children()}</Box>
      </Dialog>
    </>
  );
}

/** Bordered chart card with a title/total header and a button that opens the chart full screen. */
export function ChartCard({ title, subtitle, children }: ChartCardProps) {
  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.vars.palette.md.border}`,
        borderRadius: radius.card,
        minWidth: 0,
        overflow: 'hidden',
      })}
    >
      <CardHead
        title={title}
        subtitle={subtitle}
        action={
          <ChartExpandButton title={title} subtitle={subtitle}>
            {() => children(true)}
          </ChartExpandButton>
        }
      />
      <Box sx={{ p: '10px 12px 6px' }}>{children(false)}</Box>
    </Box>
  );
}
